import React, { useState } from 'react';
import { ScenarioLines } from './ScenarioComparison';
import AssumedDefaultsNotice from './AssumedDefaultsNotice';
import { GrowthRateInputs, parseGrowthRates, missingPricesAsOf } from '../utils/growthRates';

// F18: compare 2-4 configurations side by side. A configuration is a saved
// property - copy one and change its financing or use to make a variant. The
// backend (/compare) runs each on its own with the same window and growth rates.

interface Props {
  properties: any[];          // the selected properties (with events/owner_stays)
  startDate: string;
  endDate: string;
  granularity: string;
  growthRates: GrowthRateInputs;
}

const fmtEur = (v: number | null | undefined) => (v == null ? 'n/a' : '€' + Math.round(v).toLocaleString());

const CompareConfigurations: React.FC<Props> = ({ properties, startDate, endDate, granularity, growthRates }) => {
  const [results, setResults] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (properties.length < 2 || properties.length > 4) {
      setError('Select 2 to 4 properties above to compare them as configurations.');
      return;
    }
    const growth = parseGrowthRates(growthRates);
    if (!growth.ok) { setError(growth.error); return; }
    const undated = missingPricesAsOf(properties);
    if (undated.length > 0) { setError(`Set "Prices As Of" on: ${undated.join(', ')}`); return; }
    setLoading(true); setError(null); setResults(null);
    try {
      const base = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const resp = await fetch(`${base}/api/v1/portfolio/compare`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ properties, start_date: startDate, end_date: endDate, granularity, ...growth.rates }),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => null);
        throw new Error(body?.detail || `HTTP ${resp.status}`);
      }
      setResults(await resp.json());
    } catch (e: any) {
      setError(e?.message || 'Comparison failed');
    } finally {
      setLoading(false);
    }
  };

  const rows: { label: string; value: (c: any) => string }[] = [
    { label: 'Cash invested', value: (c) => fmtEur(c.cash_invested_eur) },
    { label: 'After-tax total', value: (c) => fmtEur(c.final_after_tax_total) },
    { label: 'IRR after tax', value: (c) => (c.irr_after_tax_pct != null ? `${c.irr_after_tax_pct.toFixed(2)}%` : 'n/a') },
    { label: 'Steady annual net (after income tax)', value: (c) => fmtEur(c.steady_state_annual_net_eur) },
    { label: 'Net sale (after CGT and loan)', value: (c) => fmtEur(c.net_sale_proceeds_eur) },
  ];

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
      <div className="flex justify-between items-center mb-2">
        <h2 className="text-2xl font-semibold text-gray-800">Compare Configurations</h2>
        <button
          onClick={run}
          disabled={loading}
          className="bg-vista-teal-600 text-white px-4 py-2 rounded-md hover:bg-vista-teal-700 transition-colors disabled:opacity-50"
        >
          {loading ? 'Comparing…' : `Compare selected (${properties.length})`}
        </button>
      </div>
      <p className="text-sm text-gray-600 mb-4">
        Each selected property is one configuration (copy a property and change its financing or use to make a variant).
        All run with the same dates and growth rates.
      </p>
      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">{error}</div>}
      {results && (
        <>
          <AssumedDefaultsNotice assumed={results.assumed_defaults} className="mb-4" />
          <div className="overflow-x-auto border border-gray-200 rounded-lg mb-4">
            <table className="min-w-full text-sm divide-y divide-gray-200">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-4 py-2 text-left font-medium"></th>
                  {results.configurations.map((c: any) => (
                    <th key={c.name} className="px-4 py-2 text-right font-medium">{c.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((r) => (
                  <tr key={r.label}>
                    <td className="px-4 py-2 text-gray-700">{r.label}</td>
                    {results.configurations.map((c: any) => (
                      <td key={c.name} className="px-4 py-2 text-right" title={r.label === 'IRR after tax' ? c.irr_note || '' : ''}>
                        {r.value(c)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-sm text-gray-600 mb-1">Cumulative after-tax cashflow</div>
          <ScenarioLines scenarios={results.configurations} />
        </>
      )}
    </div>
  );
};

export default CompareConfigurations;
